// Chat.tsx
import React, { useRef, useState, useEffect, useMemo } from 'react';
import InputBox, { type FocusedDocumentChip } from './components/InputBox';
import LoadingDots from './components/LoadingDots';
import PromptLibraryModal from './components/PromptLibraryModal';
import AgentMarkdown, { CHAT_HUMAN_TEXT_CLASS, CHAT_STRUCTURE_TITLE_CLASS } from './components/AgentMarkdown';
import ReactMarkdown from 'react-markdown';
import { Pie, Bar, Line } from 'react-chartjs-2';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getUserFriendlyErrorMessage } from '../../utils/errorMessages';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    ArcElement,
    BarElement,
    PointElement,
    LineElement,
    Tooltip,
    Legend,
} from 'chart.js';
import { normalizeChartData } from '../../utils/chartUtils';
import chatApi from '../../api/chatApi';
import googleSheetsIntegrationApi from '../../api/googleSheetsIntegrationApi';
import documentsApi from '../../api/documentsApi';
import qboApi from '../../api/qboApi';
import {
    AnswerMeta,
    FeedbackReasonCode,
    FeedbackType,
    RefreshVariant,
    AgentStateResponse,
    AgentTraceResponse,
    SheetEditIterationSummary,
} from '../../api/types';
import { File, RefreshCw, Sparkles, X, LibraryBig, Plus, Maximize2, SquarePen, Library, Expand, Minimize2, Copy, ThumbsUp, ThumbsDown, Sheet, FileText, CheckCircle2, Search, Database, ShieldCheck, Brain, LoaderCircle, ChevronRight, ChevronDown } from 'lucide-react'
import { useIsMobile } from '../../utils/hooks';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useUser } from '../../context/UserContext';


ChartJS.register(
    CategoryScale,
    LinearScale,
    ArcElement,
    BarElement,
    PointElement,
    LineElement,
    Tooltip,
    Legend
);

interface Message {
    role: 'ai' | 'human';
    content: string;
    tables?: any[];
    charts?: any[];
    suggested_questions?: string[];
    activity?: ActivityItem[];
    message_id?: string;
    parent_user_message_id?: string;
    answer_meta?: AnswerMeta;
    revisions?: any[];
    latest_feedback?: any;
    feedback_state?: {
        submitted: boolean;
        feedback: FeedbackType;
        reason_code?: FeedbackReasonCode;
    };
}

type ActivityItem = {
    type: string;
    message: string;
    status: 'active' | 'done';
};

type ActivityLike = ActivityItem | string | { type?: string; message?: string; status?: 'active' | 'done' };

interface ChatResponseData {
    text: string;
    tables?: any[];
    charts?: any[];
    suggested_questions?: string[];
    meta?: AnswerMeta;
}

interface SendMessageOptions {
    focusedDocuments?: FocusedDocumentChip[];
}

/** Proposal id is embedded in assistant markdown (see sheet_edit_service preview). */
function extractSheetProposalIdFromContent(content: string): string | null {
    const m1 = content.match(/Proposal id \(for support \/ revert API\): `([a-f0-9]{24})`/i);
    if (m1?.[1]) return m1[1];
    const m2 = content.match(/Proposal id[^:]*:\s*`([a-f0-9]{24})`/i);
    return m2?.[1] ?? null;
}

function looksLikeSheetProposalAssistantMessage(content: string): boolean {
    const l = content.toLowerCase();
    return (
        l.includes('planned updates') ||
        l.includes('proposal id') ||
        (l.includes('google sheet') && (l.includes('approve') || l.includes('reject')))
    );
}

// --- Helper for File Icons ---
const getFileIconConfig = (fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    if (['csv', 'xls', 'xlsx'].includes(extension || '')) {
        return { Icon: Sheet, color: 'text-green-600', bg: 'bg-green-50' };
    }
    return { Icon: FileText, color: 'text-red-600', bg: 'bg-red-50' };
};

const normalizeActivityItem = (item: ActivityLike): ActivityItem => {
    if (typeof item === 'string') {
        return {
            type: 'status',
            message: item,
            status: 'done',
        };
    }

    return {
        type: item.type || 'status',
        message: item.message || '',
        status: item.status === 'active' ? 'active' : 'done',
    };
};

const getActivityPresentation = (item: ActivityItem) => {
    const stepType = item.type || 'status';
    if (item.status === 'done') {
        return {
            Icon: CheckCircle2,
            iconClassName: 'text-gray-500',
        };
    }

    if (stepType.includes('supervisor')) {
        return {
            Icon: Search,
            iconClassName: 'text-gray-500',
        };
    }
    if (stepType.includes('memory')) {
        return {
            Icon: Brain,
            iconClassName: 'text-gray-500',
        };
    }
    if (stepType.includes('readiness')) {
        return {
            Icon: ShieldCheck,
            iconClassName: 'text-gray-500',
        };
    }
    if (stepType.includes('resolver') || stepType.includes('tool')) {
        return {
            Icon: Database,
            iconClassName: 'text-gray-500',
        };
    }
    return {
        Icon: Sparkles,
        iconClassName: 'text-gray-500',
    };
};

const appendActivityStep = (steps: ActivityItem[], event: { type: string; message: string }) => {
    const completed = steps.map((step) => ({ ...step, status: 'done' as const }));
    const last = completed[completed.length - 1];
    if (last && last.type === event.type && last.message === event.message) {
        return completed;
    }
    return [
        ...completed,
        {
            type: event.type,
            message: event.message,
            status: 'active' as const,
        },
    ];
};

const finalizeActivitySteps = (steps: ActivityItem[]) =>
    steps.map((step) => ({ ...step, status: 'done' as const }));


interface ChatProps {
    messages?: Message[];
    conversationId?: string;
    page: 'margin' | 'capital' | 'cashflow' | 'documents' | 'signals';
    year?: number;
    month?: number;
    initialPrompts?: string[];
    onClose?: () => void;
    /** When false, skips documents workspace summary fetch (e.g. AI CFO panel closed on /documents). */
    sidebarOpen?: boolean;
    /** Documents: refresh file list after Google Sheets import (passed to chat input). */
    onDocumentsRefresh?: () => void;
    /** Documents: after Picker queues sheet imports, e.g. open the Google Sheets folder in the drive. */
    onGoogleSheetsImportQueued?: () => void;
    /** Documents: folder/category label for the CFO context chip (e.g. "All folders"). */
    documentsFolderLabel?: string;
    /** When set (e.g. user opened a Google Sheet in the workspace), steers sheet edit agent without new UI. */
    activeGoogleSheet?: { sourceDocumentId?: string; googleFileId?: string };
    /** Optional hidden context appended to outgoing questions for selected-scope chat. */
    contextPrompt?: string;
    contextId?: string;
    contextLabel?: string;
    onClearContext?: () => void;
    contextOptions?: Array<{ id: string; label: string }>;
    onSelectContext?: (id: string) => void;
}

// --- Sub-Component for AI Message ---
const AiMessage: React.FC<{
    msg: Message;
    onSuggestionClick: (q: string) => void;
    onRecycleSuggestions: () => void;
    onFeedback: (feedback: FeedbackType, reasonCode?: FeedbackReasonCode, reasonNote?: string) => Promise<void>;
    onRefresh: (variant: RefreshVariant) => Promise<void>;
    readOnly?: boolean;
    showSuggestedQuestions?: boolean;
}> = ({ msg, onSuggestionClick, onRecycleSuggestions, onFeedback, onRefresh, readOnly, showSuggestedQuestions }) => {
    const [copied, setCopied] = useState(false);
    const [showActivity, setShowActivity] = useState(false);
    const [showBadMenu, setShowBadMenu] = useState(false);
    const [showRefreshMenu, setShowRefreshMenu] = useState(false);
    const [reasonCode, setReasonCode] = useState<FeedbackReasonCode>('wrong_period');
    const [reasonNote, setReasonNote] = useState('');
    const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const activityWasActiveRef = useRef(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const activity = (msg.activity ?? []).map(normalizeActivityItem).filter((item) => item.message);
    const hasMessageId = Boolean(msg.message_id);
    const suggestedQuestions = (msg.suggested_questions || []).filter(Boolean).slice(0, 3);

    useEffect(() => {
        const hasActive = activity.some((item) => item.status === 'active');
        if (hasActive) {
            activityWasActiveRef.current = true;
            setShowActivity(true);
            return;
        }
        if (activity.length > 0 && activityWasActiveRef.current) {
            setShowActivity(false);
            activityWasActiveRef.current = false;
            return;
        }
        if (activity.length === 0) {
            activityWasActiveRef.current = false;
        }
    }, [activity]);

    useEffect(() => {
        if (!showActivity || activity.length === 0) return;
        containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, [showActivity, activity.length]);

    const handleCopy = () => {
        navigator.clipboard.writeText(msg.content);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const submitGoodFeedback = async () => {
        if (!hasMessageId || readOnly || isSubmittingFeedback) return;
        setIsSubmittingFeedback(true);
        try {
            await onFeedback('good');
        } finally {
            setIsSubmittingFeedback(false);
        }
    };

    const submitBadFeedback = async () => {
        if (!hasMessageId || readOnly || isSubmittingFeedback) return;
        setIsSubmittingFeedback(true);
        try {
            await onFeedback('bad', reasonCode, reasonCode === 'other' ? reasonNote : undefined);
            setShowBadMenu(false);
        } finally {
            setIsSubmittingFeedback(false);
        }
    };

    const runRefresh = async (variant: RefreshVariant) => {
        if (!hasMessageId || readOnly || isRefreshing) return;
        setIsRefreshing(true);
        try {
            await onRefresh(variant);
            setShowRefreshMenu(false);
        } finally {
            setIsRefreshing(false);
        }
    };

    return (
        <div ref={containerRef} className="w-full space-y-3 scroll-mb-32 text-sm text-gray-900">
            {activity.length > 0 && (
                <div className="rounded-lg border border-gray-200 bg-gray-50">
                    <button
                        type="button"
                        onClick={() => setShowActivity((prev) => !prev)}
                        className="flex w-full items-center justify-between px-3 py-2 text-left"
                    >
                        <div className="min-w-0">
                            <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-gray-500">
                                Activity
                            </div>
                            <div className="mt-0.5 truncate text-sm text-gray-600">
                                {activity[activity.length - 1]?.message}
                            </div>
                        </div>
                        <div className="ml-3 flex items-center gap-2 text-xs text-gray-500">
                            <span>{activity.length} step{activity.length === 1 ? '' : 's'}</span>
                            {showActivity ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </div>
                    </button>

                    {showActivity && (
                        <div className="border-t border-gray-200 px-3 py-2">
                            <div className="space-y-2">
                                {activity.map((step, index) => {
                                    const { Icon, iconClassName } = getActivityPresentation(step);
                                    const isLast = index === activity.length - 1;
                                    return (
                                        <div key={`${step.type}-${step.message}-${index}`} className="flex gap-3">
                                            <div className="flex flex-col items-center">
                                                <div className="flex h-6 w-6 items-center justify-center rounded-full border border-gray-200 bg-white">
                                                    {step.status === 'active' ? (
                                                        <LoaderCircle className={`h-3.5 w-3.5 animate-spin ${iconClassName}`} />
                                                    ) : (
                                                        <Icon className={`h-3.5 w-3.5 ${iconClassName}`} />
                                                    )}
                                                </div>
                                                {!isLast && <div className="mt-1 h-4 w-px bg-gray-200" />}
                                            </div>
                                            <div className="pt-0.5">
                                                <div className="text-sm text-gray-700">{step.message}</div>
                                                <div className="mt-0.5 text-xs text-gray-400">
                                                    {step.status === 'active' ? 'In progress' : 'Completed'}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            )}

            <AgentMarkdown content={msg.content} />

            {msg.tables?.map((table, index) => (
                <div key={`table-${index}`}>
                    {table.title && (
                        <h3 className={CHAT_STRUCTURE_TITLE_CLASS}>
                            <ReactMarkdown>{table.title}</ReactMarkdown>
                        </h3>
                    )}
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse border border-gray-300 text-sm">
                            <thead className="bg-gray-100">
                                <tr>
                                    {table.headers.map((header: string, i: number) => (
                                        <th key={i} className="border border-gray-300 px-3 py-2 text-left">
                                            <ReactMarkdown>{header}</ReactMarkdown>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {table.rows.map((row: string[], rowIdx: number) => (
                                    <tr key={rowIdx}>
                                        {row.map((cell, colIdx) => (
                                            <td key={colIdx} className="border border-gray-300 px-3 py-2">
                                                {cell}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ))}

            {msg.charts?.map((rawChart, index) => {
                const chart = normalizeChartData(rawChart);
                const ChartComponent = chart.type === 'bar' ? Bar : chart.type === 'line' ? Line : Pie;
                const isValid = chart?.data?.labels && Array.isArray(chart.data?.datasets);

                if (!isValid) return <div key={`chart-${index}`} className="text-red-500 text-sm">⚠️ Invalid chart data</div>;

                return (
                    <div key={`chart-${index}`} className="mt-4">
                        {chart.title && (
                            <h3 className={CHAT_STRUCTURE_TITLE_CLASS}>
                                <ReactMarkdown>{chart.title}</ReactMarkdown>
                            </h3>
                        )}
                        <div className="w-full max-w-lg mx-auto overflow-x-auto">
                            <ChartComponent data={chart.data} options={chart.options} />
                        </div>
                    </div>
                );
            })}

            {msg.content && (
                <div className="flex items-center gap-1 mt-4 pt-2 relative">
                    <button
                        onClick={handleCopy}
                        className="p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors flex items-center gap-1.5 text-xs font-medium"
                        title="Copy to clipboard"
                    >
                        <Copy className="w-4 h-4" />
                        {copied && <span className="ml-1 text-green-600">Copied</span>}
                    </button>
                    <button
                        className="p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors disabled:text-gray-300 disabled:hover:bg-transparent"
                        title={hasMessageId ? "Good response" : "Unavailable for legacy messages"}
                        disabled={!hasMessageId || !!readOnly || isSubmittingFeedback}
                        onClick={submitGoodFeedback}
                    >
                        <ThumbsUp className="w-4 h-4" />
                    </button>
                    <button
                        className="p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors disabled:text-gray-300 disabled:hover:bg-transparent"
                        title={hasMessageId ? "Bad response" : "Unavailable for legacy messages"}
                        disabled={!hasMessageId || !!readOnly || isSubmittingFeedback}
                        onClick={() => {
                            setShowBadMenu((prev) => !prev);
                            setShowRefreshMenu(false);
                        }}
                    >
                        <ThumbsDown className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => {
                            setShowRefreshMenu((prev) => !prev);
                            setShowBadMenu(false);
                        }}
                        className="p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors disabled:text-gray-300 disabled:hover:bg-transparent"
                        title={hasMessageId ? "Refresh answer" : "Unavailable for legacy messages"}
                        disabled={!hasMessageId || !!readOnly || isRefreshing}
                    >
                        <RefreshCw className="w-4 h-4" />
                    </button>
                    <button
                        onClick={onRecycleSuggestions}
                        className="p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                        title="Get new suggestions"
                    >
                        <Library size={16} />
                    </button>

                    {msg.feedback_state?.submitted && (
                        <span className="ml-2 text-xs text-gray-500">
                            Feedback submitted
                        </span>
                    )}

                    {showBadMenu && hasMessageId && !readOnly && (
                        <div className="absolute left-0 top-9 z-20 w-72 rounded-lg border border-gray-200 bg-white p-3 shadow-lg">
                            <div className="text-xs font-semibold text-gray-700 mb-2">Why was this bad?</div>
                            <select
                                value={reasonCode}
                                onChange={(e) => setReasonCode(e.target.value as FeedbackReasonCode)}
                                className="w-full rounded border border-gray-300 px-2 py-1 text-sm mb-2"
                            >
                                <option value="wrong_period">Wrong period</option>
                                <option value="wrong_entity">Wrong entity</option>
                                <option value="too_generic">Too generic</option>
                                <option value="hallucinated">Hallucinated</option>
                                <option value="other">Other</option>
                            </select>
                            {reasonCode === 'other' && (
                                <textarea
                                    value={reasonNote}
                                    onChange={(e) => setReasonNote(e.target.value)}
                                    className="w-full rounded border border-gray-300 px-2 py-1 text-sm mb-2"
                                    rows={2}
                                    placeholder="Optional note"
                                    maxLength={500}
                                />
                            )}
                            <div className="flex justify-end gap-2">
                                <button
                                    className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-100"
                                    onClick={() => setShowBadMenu(false)}
                                    type="button"
                                >
                                    Cancel
                                </button>
                                <button
                                    className="text-xs px-2 py-1 rounded bg-gray-900 text-white hover:bg-gray-800 disabled:bg-gray-400"
                                    disabled={isSubmittingFeedback}
                                    onClick={submitBadFeedback}
                                    type="button"
                                >
                                    Submit
                                </button>
                            </div>
                        </div>
                    )}

                    {showRefreshMenu && hasMessageId && !readOnly && (
                        <div className="absolute left-24 top-9 z-20 w-44 rounded-lg border border-gray-200 bg-white p-2 shadow-lg">
                            <button className="w-full text-left text-sm px-2 py-1 rounded hover:bg-gray-100" onClick={() => runRefresh('try_again')} type="button">
                                Try again
                            </button>
                            {/* <button className="w-full text-left text-sm px-2 py-1 rounded hover:bg-gray-100" onClick={() => runRefresh('longer')} type="button">
                                Longer
                            </button>
                            <button className="w-full text-left text-sm px-2 py-1 rounded hover:bg-gray-100" onClick={() => runRefresh('shorter')} type="button">
                                Shorter
                            </button> */}
                        </div>
                    )}
                </div>
            )}

            {showSuggestedQuestions && suggestedQuestions.length > 0 && (
                <div className="mt-2 space-y-2">
                    {suggestedQuestions.map((question, index) => (
                        <button
                            key={`${question}-${index}`}
                            type="button"
                            onClick={() => onSuggestionClick(question)}
                            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-left text-sm text-gray-700 transition hover:border-gray-300 hover:bg-gray-50"
                        >
                            {question}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};



const CHAT_STORAGE = {
    margin: {
        msgs: "margin_chat_messages",
        id: "margin_conversation_id",
    },
    capital: {
        msgs: "capital_chat_messages",
        id: "capital_conversation_id",
    },
    cashflow: {
        msgs: "cashflow_chat_messages",
        id: "cashflow_conversation_id",
    },
    documents: {
        msgs: "documents_chat_messages",
        id: "documents_conversation_id",
    },
    signals: {
        msgs: "signals_chat_messages",
        id: "signals_conversation_id",
    },
} as const;

type PageKey = keyof typeof CHAT_STORAGE;

// =====================================================================
// Document-agent v2 helper components
// Rendered inline so all chat panel state stays colocated; kept tiny and
// dependency-free so they compose with the existing Approve/Reject card.
// =====================================================================

/**
 * Escape-hatch dialog the orchestrator parks in agent state when it cannot
 * plan a safe edit (ambiguous scope, destructive pattern, etc). The user
 * either picks one of the suggested answers / types a free-text reply
 * (submitted as the next chat turn so the normal flow resumes and
 * `consume_pending_clarification` fires), or cancels to drop the question.
 */
/**
 * Unified dropdown-driven clarification panel.
 *
 * One visual covers both surfaces:
 *   1. Edit-path escape-hatch: ``agentState.pending_clarification`` — the
 *      orchestrator is blocked on a question it cannot resolve safely.
 *   2. Read-path schema confirmation: a tab's detected schema was low
 *      confidence, the answer was produced anyway, and we passively ask
 *      the user to confirm / correct via
 *      ``answer_meta.sheet_schema_low_confidence``.
 *
 * The dropdown has two entries: "Approve" (confirm what we have) and
 * "Make correction" (reveal a free-text input). This matches the
 * Approve/Reject card the user already recognizes so aesthetics stay
 * consistent with the rest of the chat surface.
 */
const ClarificationCard: React.FC<{
    question: string;
    originalRequest?: string;
    subtitle?: React.ReactNode;
    choices?: string[];
    approveLabel?: string;
    correctLabel?: string;
    correctPlaceholder?: string;
    busy?: boolean;
    onApprove?: () => void;
    onCorrect: (text: string) => void;
    onDismiss?: () => void;
    footer?: React.ReactNode;
}> = ({
    question,
    originalRequest,
    subtitle,
    choices,
    approveLabel = 'Approve',
    correctLabel = 'Make correction',
    correctPlaceholder = 'e.g. headers are in row 2, or row 1 and row 2',
    busy,
    onApprove,
    onCorrect,
    onDismiss,
    footer,
}) => {
        const [mode, setMode] = useState<'approve' | 'correct'>(onApprove ? 'approve' : 'correct');
        const [freeText, setFreeText] = useState('');
        const safeChoices = (choices || []).filter(Boolean);
        const primaryLabel = mode === 'approve' ? 'Confirm' : 'Submit correction';
        const primaryDisabled = busy || (mode === 'correct' && !freeText.trim()) || (mode === 'approve' && !onApprove);

        const handlePrimary = () => {
            if (busy) return;
            if (mode === 'approve') {
                onApprove?.();
            } else if (freeText.trim()) {
                onCorrect(freeText.trim());
                setFreeText('');
            }
        };

        return (
            <div className="my-3 w-full max-w-6xl overflow-hidden rounded-xl border border-slate-200 bg-gradient-to-b from-white to-slate-50/60 px-4 py-3 shadow-sm sm:px-5 sm:py-4">
                <div className="mb-2 flex items-start gap-2.5">
                    <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-indigo-50 text-indigo-600">
                        <Brain className="h-3.5 w-3.5" aria-hidden />
                    </div>
                    {/* min-w-0 is critical: without it, flex children won't shrink
                    below their content, so ``truncate`` on the subtitle row
                    silently overflows the card. */}
                    <div className="min-w-0 flex-1">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                            Quick question
                        </p>
                        <p className="mt-0.5 text-sm font-medium text-slate-900">{question}</p>
                        {subtitle && (
                            <div className="mt-1 space-y-0.5 text-xs leading-snug text-slate-500">
                                {subtitle}
                            </div>
                        )}
                        {originalRequest && (
                            <p className="mt-1 truncate text-[11px] italic text-slate-500">
                                re: &ldquo;{originalRequest}&rdquo;
                            </p>
                        )}
                    </div>
                </div>
                {safeChoices.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                        {safeChoices.map((choice) => (
                            <button
                                key={choice}
                                type="button"
                                disabled={busy}
                                onClick={() => onCorrect(choice)}
                                className="rounded-full border border-gray-300 bg-white px-3 py-1 text-xs font-medium text-slate-800 shadow-sm transition hover:border-slate-500 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                {choice}
                            </button>
                        ))}
                    </div>
                )}
                <div className="mt-3 flex flex-wrap items-center gap-2">
                    <select
                        aria-label="Clarification action"
                        value={mode}
                        onChange={(e) => setMode(e.target.value as 'approve' | 'correct')}
                        disabled={busy}
                        className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-slate-900 shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-400 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        {onApprove && <option value="approve">{approveLabel}</option>}
                        <option value="correct">{correctLabel}</option>
                    </select>
                    {mode === 'correct' && (
                        <input
                            type="text"
                            value={freeText}
                            onChange={(e) => setFreeText(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey && freeText.trim() && !busy) {
                                    e.preventDefault();
                                    handlePrimary();
                                }
                            }}
                            placeholder={correctPlaceholder}
                            disabled={busy}
                            className="min-w-[220px] flex-1 rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-400 disabled:cursor-not-allowed disabled:opacity-60"
                        />
                    )}
                    <button
                        type="button"
                        disabled={primaryDisabled}
                        onClick={handlePrimary}
                        className="rounded-md border border-slate-800 bg-slate-900 px-3 py-1.5 text-sm font-medium text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        {primaryLabel}
                    </button>
                    {onDismiss && (
                        <button
                            type="button"
                            disabled={busy}
                            onClick={onDismiss}
                            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            Dismiss
                        </button>
                    )}
                </div>
                {footer && (
                    <div className="mt-3 border-t border-slate-200/70 pt-2 text-[11px] leading-snug text-slate-500">
                        {footer}
                    </div>
                )}
            </div>
        );
    };

/** Map append-only trace event phases to a compact user-facing status.
 *
 * NOTE: this helper is deliberately narrow. The main "Activity" timeline
 * (driven off SSE ``status`` events) is the single source of truth for
 * live progress in a turn — showing a second, polled progress strip while
 * that timeline is animating was causing two indicators to run side by
 * side (e.g. "Drafting a grounded response…" + "Planning the edit…" at
 * the same time, or the scary "Hit an error; falling back." appearing
 * next to a perfectly fine streaming response). We now only surface
 * PERSISTENT state from the trace: "ready for your review" (the user
 * needs to click something) and "needs your input" (clarification
 * card). Transient phase chatter stays invisible.
 */
function deriveAgentStatusMessage(
    trace: AgentTraceResponse | undefined,
    awaitingRunId: string | null,
): string | null {
    if (awaitingRunId) return 'Ready for your review.';
    if (!trace || !trace.events || trace.events.length === 0) return null;
    const latest = trace.events[0];
    const phase = String(latest?.phase || '').toLowerCase();
    if (phase === 'ready' || phase === 'ready_for_review') {
        return 'Ready for your review.';
    }
    if (phase === 'clarify' || phase === 'clarification_needed') {
        return 'Needs your input.';
    }
    return null;
}

/**
 * Lightweight status line shown while the v2 orchestrator is iterating.
 * Pulled from agent-trace (poll) rather than SSE so it works even when the
 * stream drops mid-run; renders nothing when there's no active activity.
 */
const AgentStatusStrip: React.FC<{ message: string | null }> = ({ message }) => {
    if (!message) return null;
    return (
        <div className="my-2 flex items-center gap-2 rounded-md border border-indigo-100 bg-indigo-50 px-3 py-1.5 text-xs text-indigo-800">
            <LoaderCircle className="h-3.5 w-3.5 animate-spin" aria-hidden />
            <span>{message}</span>
        </div>
    );
};

/**
 * Collapsible per-iteration diff summary rendered inside the v2 Approve/Reject
 * card. Each iteration lists the user-facing summary string, the cell count,
 * and the touched ranges so the user can trust-but-verify a multi-pass run.
 */
const IterationSummaryList: React.FC<{ summaries: SheetEditIterationSummary[] }> = ({ summaries }) => {
    const [open, setOpen] = useState(false);
    if (!summaries || summaries.length === 0) return null;
    const total = summaries.reduce((s, it) => s + (it.cells_touched || 0), 0);
    return (
        <div className="mt-2 rounded-md border border-slate-200 bg-white/80 px-2 py-1.5">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="flex w-full items-center justify-between gap-2 text-[11px] font-medium text-slate-700 hover:text-slate-900"
            >
                <span className="flex items-center gap-1">
                    {open ? <ChevronDown className="h-3.5 w-3.5" aria-hidden /> : <ChevronRight className="h-3.5 w-3.5" aria-hidden />}
                    What changed in each pass
                </span>
                <span className="text-[10px] tabular-nums text-slate-500">
                    {summaries.length} pass{summaries.length === 1 ? '' : 'es'} · {total} cell{total === 1 ? '' : 's'}
                </span>
            </button>
            {open && (
                <ul className="mt-1 space-y-1 pl-4">
                    {summaries.map((it) => (
                        <li key={it.iteration_num} className="text-[11px] leading-snug text-slate-700">
                            <span className="font-semibold tabular-nums text-slate-500">#{it.iteration_num}</span>{' '}
                            {it.summary || 'Applied edit'}
                            {it.cells_touched ? (
                                <span className="ml-1 text-slate-500">({it.cells_touched} cell{it.cells_touched === 1 ? '' : 's'})</span>
                            ) : null}
                            {it.ranges && it.ranges.length > 0 ? (
                                <span className="ml-1 text-[10px] text-slate-400">— {it.ranges.slice(0, 3).join(', ')}{it.ranges.length > 3 ? '…' : ''}</span>
                            ) : null}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

const ChatPanel: React.FC<ChatProps> = ({
    messages: savedMessages,
    conversationId,
    page,
    year,
    month,
    initialPrompts,
    onClose,
    sidebarOpen = true,
    onDocumentsRefresh,
    onGoogleSheetsImportQueued,
    documentsFolderLabel,
    activeGoogleSheet,
    contextPrompt,
    contextId,
    contextLabel,
    onClearContext,
    contextOptions,
    onSelectContext,
}) => {
    const isMobile = useIsMobile();
    const { userMetadata } = useUser();

    // Helper to read/write safely
    const readJSON = <T,>(key: string, fallback: T): T => {
        try {
            const raw = sessionStorage.getItem(key);
            return raw ? (JSON.parse(raw) as T) : fallback;
        } catch {
            return fallback;
        }
    };

    const pageKey = page as PageKey; // ensure 'margin' | 'capital'

    /** Session chat keys are namespaced by Finz user id so another account on the same browser tab cannot see prior chat UI. */
    const chatStorageKeys = useMemo(() => {
        if (!CHAT_STORAGE[pageKey]) return null;
        const uid = userMetadata?.user_id;
        if (!uid) return null;
        const base = CHAT_STORAGE[pageKey];
        return {
            msgs: `${uid}__${base.msgs}`,
            id: `${uid}__${base.id}`,
        };
    }, [pageKey, userMetadata?.user_id]);

    const [messages, setMessages] = useState<Message[]>(savedMessages || []);
    const [focusedDocuments, setFocusedDocuments] = useState<FocusedDocumentChip[]>([]);

    const [currConversationId, setCurrConversationId] = useState<string | null>(conversationId || null);

    /** After approve/reject via the sheet decision control, hide the control for that proposal id. */
    const [resolvedSheetProposalIds, setResolvedSheetProposalIds] = useState<Record<string, true>>({});

    const [sheetDecisionBusyProposalId, setSheetDecisionBusyProposalId] = useState<string | null>(null);

    /** Document-agent v2: the aggregate run id is approved/reverted as one unit. */
    const [resolvedSheetRunIds, setResolvedSheetRunIds] = useState<Record<string, true>>({});
    const [sheetDecisionBusyRunId, setSheetDecisionBusyRunId] = useState<string | null>(null);
    /** Optimistic local dismiss while the backend TTL catches up on the pending clarification. */
    const [dismissedClarificationKeys, setDismissedClarificationKeys] = useState<Record<string, true>>({});

    const { activeBusinessId } = useWorkspace();

    // Hydrate from sessionStorage once we know the logged-in user (keys are per-user).
    useEffect(() => {
        if (!chatStorageKeys) return;
        const msgs = readJSON<Message[]>(chatStorageKeys.msgs, savedMessages || []);
        const id = sessionStorage.getItem(chatStorageKeys.id) || conversationId || null;
        setMessages(msgs);
        setCurrConversationId(id);
    }, [chatStorageKeys, conversationId, savedMessages]);
    const queryClient = useQueryClient();
    const backendPage = page === 'margin' ? 'Margin' : 'Cashflow';
    const d = new Date();
    const y = year || d.getFullYear();
    const m = month || d.getMonth() + 1;

    const showCashflowStyleSummary = page === 'margin' || page === 'cashflow' || page === 'signals';

    useEffect(() => {
        // Keep focus chips scoped to the documents assistant only.
        if (page !== 'documents' && focusedDocuments.length > 0) {
            setFocusedDocuments([]);
        }
    }, [page, focusedDocuments.length]);

    const { data: summaryData, isFetching: isSummaryLoading, refetch: refetchSummary } = useQuery({
        queryKey: ['pageSummary', backendPage, y, m, activeBusinessId],
        queryFn: () => chatApi.calculatePageSummary(backendPage, y, m, false, activeBusinessId || undefined),
        enabled: showCashflowStyleSummary,
        staleTime: 1000 * 60 * 5,
        refetchInterval: (query: any) =>
            query?.state?.data?.summary?.includes('is generating the latest financial')
                ? 5000 : false,
    });

    const pageSummary = summaryData?.summary || null;

    const {
        data: documentsWorkspaceSummary,
        isFetching: isDocumentsWorkspaceSummaryLoading,
        refetch: refetchDocumentsWorkspaceSummary,
    } = useQuery({
        queryKey: ['workspace-summary', activeBusinessId],
        queryFn: () => documentsApi.getWorkspaceSummary(activeBusinessId || undefined),
        enabled: page === 'documents' && Boolean(activeBusinessId) && sidebarOpen,
        staleTime: 1000 * 60 * 5,
    });

    const lastAiMessageIndex = useMemo(() => {
        for (let i = messages.length - 1; i >= 0; i--) {
            if (messages[i]?.role === 'ai') return i;
        }
        return -1;
    }, [messages]);

    const { data: pendingSheetProposal } = useQuery({
        queryKey: ['google-sheet-edit-pending', currConversationId, page],
        queryFn: () => googleSheetsIntegrationApi.getPendingSheetEdit(currConversationId!),
        enabled: page === 'documents' && Boolean(currConversationId),
        staleTime: 10_000,
    });

    const { data: awaitingConfirmationSheetProposal } = useQuery({
        queryKey: ['google-sheet-edit-awaiting-confirmation', currConversationId, page],
        queryFn: () =>
            googleSheetsIntegrationApi.getAwaitingConfirmationSheetEdit(currConversationId!),
        enabled: page === 'documents' && Boolean(currConversationId),
        staleTime: 10_000,
    });

    // Document-agent v2: tenant-scoped snapshot of pending clarification +
    // latest aggregate awaiting-confirmation run. Poll faster (5s) while
    // something is pending so the UI stays in sync without SSE.
    const { data: agentState, refetch: refetchAgentState } = useQuery<AgentStateResponse>({
        queryKey: ['google-sheet-agent-state', currConversationId, page],
        queryFn: () => googleSheetsIntegrationApi.getAgentState(currConversationId!),
        enabled: page === 'documents' && Boolean(currConversationId),
        staleTime: 5_000,
        refetchInterval: (query: any) => {
            const d = query?.state?.data as AgentStateResponse | undefined;
            if (d?.pending_clarification || d?.awaiting_confirmation_run) return 5_000;
            return false;
        },
    });

    const handleRefreshSummary = async () => {
        try {
            // We pass true to force a backend recalculation
            await chatApi.calculatePageSummary(backendPage, y, m, true, activeBusinessId || undefined);
            refetchSummary();
        } catch (err) {
            console.error("Failed to refresh summary", err);
        }
    };

    // Save messages and conversation_id (per Finz user)
    useEffect(() => {
        if (!chatStorageKeys) return;
        sessionStorage.setItem(chatStorageKeys.msgs, JSON.stringify(messages));
    }, [messages, chatStorageKeys]);
    useEffect(() => {
        if (!chatStorageKeys || !currConversationId) return;
        sessionStorage.setItem(chatStorageKeys.id, currConversationId);
    }, [currConversationId, chatStorageKeys]);

    const [isLoading, setIsLoading] = useState(false);
    /** When false, hide approve/reject sheet controls; backend serves read-only agent. */
    const [cfoAgentWritesEnabled, setCfoAgentWritesEnabled] = useState(false);
    const [showPromptLibrary, setShowPromptLibrary] = useState(false);
    const [initPrompts, setInitPrompts] = useState<string[]>([]);
    const [isExpanded, setIsExpanded] = useState(false);
    const chatEndRef = useRef<HTMLDivElement>(null);
    const streamAbortRef = useRef<AbortController | null>(null);
    /** Prevents superseded streams from clearing loading state or clobbering refs. */
    const streamRequestGenRef = useRef(0);
    const latestMessage = messages[messages.length - 1];
    const lastAiHasActiveActivity = Boolean(
        latestMessage?.role === 'ai' &&
        (latestMessage.activity ?? []).some((a) => a.status === 'active')
    );
    /** In-flight stream or stuck activity UI (e.g. stream ended without final). Drives Stop + submit lock on Documents. */
    const documentsAgentBusy =
        page === 'documents' && (isLoading || lastAiHasActiveActivity);

    // Append-only phase trace — drives the AgentStatusStrip while the agent is
    // mid-run. Disabled when the panel is idle so we don't poll forever.
    const { data: agentTrace } = useQuery<AgentTraceResponse>({
        queryKey: ['google-sheet-agent-trace', currConversationId, page],
        queryFn: () => googleSheetsIntegrationApi.getAgentTrace(currConversationId!, 20),
        enabled: page === 'documents' && Boolean(currConversationId) && documentsAgentBusy,
        staleTime: 1_500,
        refetchInterval: documentsAgentBusy ? 1_500 : false,
    });
    const shouldShowLoadingDots = Boolean(
        isLoading &&
        (!latestMessage ||
            latestMessage.role !== 'ai' ||
            ((latestMessage.activity?.length || 0) === 0 && !latestMessage.content))
    );

    useEffect(() => {
        if (page !== 'documents') {
            setCfoAgentWritesEnabled(false);
            return;
        }
        chatApi
            .getCfoAgentConfig()
            .then((c) => setCfoAgentWritesEnabled(c.cfo_agent_writes_enabled))
            .catch(() => setCfoAgentWritesEnabled(false));
    }, [page]);

    useEffect(() => {
        if (initialPrompts && initialPrompts.length > 0) {
            setInitPrompts(initialPrompts);
        }
    }, [initialPrompts]);

    // Fetch initial suggested prompts when panel opens with no messages
    useEffect(() => {
        if (messages.length === 0 && initPrompts.length === 0) {
            if (initialPrompts && initialPrompts.length > 0) {
                setInitPrompts(initialPrompts);
            } else {
                chatApi.getPrompts()
                    .then(res => setInitPrompts(res.map(p => p.question)))
                    .catch(err => console.error('Failed to load init prompts:', err));
            }
        }
    }, [messages.length, initialPrompts]);

    useEffect(() => {
        if (chatEndRef.current) {
            chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages, isLoading]);

    useEffect(() => {
        const handleResize = () => {
            if (window.visualViewport) {
                // Manually set the height of the chat to match exactly what's visible
                const currentHeight = window.visualViewport.height;
                // Subtract 50px on mobile to account for the mobile header
                const finalHeight = isMobile ? currentHeight - 50 : currentHeight;
                document.documentElement.style.setProperty('--chat-height', `${finalHeight}px`);
            }
        };

        // Call once initially
        handleResize();

        window.visualViewport?.addEventListener('resize', handleResize);
        return () => window.visualViewport?.removeEventListener('resize', handleResize);
    }, [isMobile]);

    // To Fix Stringified JSON AI Content
    useEffect(() => {
        if (savedMessages) {
            const normalized = savedMessages.map((msg) => {
                if (typeof msg.content === 'string') {
                    const jsonMatch = msg.content.match(/```json\n([\s\S]*?)```/);
                    if (jsonMatch) {
                        try {
                            const parsed = JSON.parse(jsonMatch[1]);
                            return {
                                role: msg.role as 'ai' | 'human',
                                content: parsed.text || '',
                                tables: parsed.tables || [],
                                charts: parsed.charts || [],
                                suggested_questions: parsed.suggested_questions || [],
                                answer_meta: parsed.meta || msg.answer_meta,
                                message_id: msg.message_id || parsed.meta?.message_id,
                                parent_user_message_id: msg.parent_user_message_id || parsed.meta?.parent_user_message_id,
                                revisions: msg.revisions || [],
                                latest_feedback: msg.latest_feedback,
                            };
                        } catch (e) {
                            console.warn("Failed to parse embedded JSON content:", e);
                        }
                    }

                    return {
                        role: msg.role as 'ai' | 'human',
                        content: msg.content,
                        message_id: msg.message_id,
                        parent_user_message_id: msg.parent_user_message_id,
                        answer_meta: msg.answer_meta,
                        revisions: msg.revisions || [],
                        latest_feedback: msg.latest_feedback,
                    };
                }
                return {
                    role: msg.role as 'ai' | 'human',
                    content: (msg.content as any).text || '',
                    tables: (msg.content as any).tables || [],
                    charts: (msg.content as any).charts || [],
                    suggested_questions: (msg.content as any).suggested_questions || [],
                    answer_meta: (msg.content as any).meta || msg.answer_meta,
                    message_id: msg.message_id || (msg.content as any).meta?.message_id,
                    parent_user_message_id: msg.parent_user_message_id || (msg.content as any).meta?.parent_user_message_id,
                    revisions: msg.revisions || [],
                    latest_feedback: msg.latest_feedback,
                };
            });
            setMessages(normalized);
        }
    }, [savedMessages]);

    const AISparkle = ({ size = 18 }: { size?: number }) => (
        <svg
            width={size}
            height={size}
            viewBox="0 0 18 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="shrink-0"
        >
            <path
                d="M16.2 8.00833L13.7 6.75833L11.4667 5.64167L11.4417 5.61667L10.325 3.38333L9.075 0.883333C8.79167 0.316667 7.86667 0.316667 7.58333 0.883333L6.33333 3.38333L5.21667 5.61667L5.19167 5.64167L2.95833 6.75833L0.458333 8.00833C0.175 8.15 0 8.44167 0 8.75C0 9.05833 0.175 9.35 0.458333 9.49167L2.95833 10.7417L5.19167 11.8583L5.21667 11.8833L6.33333 14.1167L7.58333 16.6167C7.725 16.9 8.01667 17.075 8.325 17.075C8.63333 17.075 8.925 16.9 9.06667 16.6167L10.3167 14.1167L11.4333 11.8833L11.4583 11.8583L13.6917 10.7417L16.1917 9.49167C16.475 9.35 16.65 9.05833 16.65 8.75C16.65 8.44167 16.475 8.15 16.1917 8.00833H16.2ZM14.575 0L13.7917 1.71667L12.075 2.5L13.7917 3.28333L14.575 5L15.3583 3.28333L17.075 2.5L15.3583 1.71667L14.575 0Z"
                fill="url(#ai_sparkle_gradient)"
            />
            <defs>
                <linearGradient
                    id="ai_sparkle_gradient"
                    x1="0.332256"
                    y1="0"
                    x2="17.3938"
                    y2="16.3976"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop stopColor="#4143FF" />
                    <stop offset="1" stopColor="#9798FC" />
                </linearGradient>
            </defs>
        </svg>
    );

    // Ask message 
    const handleSendMessage = async (message: string, options?: SendMessageOptions) => {
        if (!message.trim()) return;
        if (page === 'documents') {
            const lm = messages[messages.length - 1];
            const alreadyBusy =
                isLoading ||
                (lm?.role === 'ai' &&
                    (lm.activity ?? []).some((a) => a.status === 'active'));
            if (alreadyBusy) return;
        }
        setInitPrompts([]);

        const gen = ++streamRequestGenRef.current;

        const newMessages: Message[] = [...messages, { role: 'human', content: message }];
        setMessages([...newMessages, { role: 'ai', content: '' }]);
        setIsLoading(true);

        let ac: AbortController | null = null;
        try {
            streamAbortRef.current?.abort();
            ac = new AbortController();
            streamAbortRef.current = ac;

            console.log("uploading msg to: ", currConversationId)

            // Use current date defaults if not provided (safe fallback)
            const d = new Date();
            const y = year || d.getFullYear();
            const m = month || d.getMonth() + 1;
            const outboundQuestion = message;

            let streamedText = '';
            const sheetPayload =
                page === 'documents' && activeGoogleSheet
                    ? {
                        google_sheet_source_document_id:
                            activeGoogleSheet.sourceDocumentId || undefined,
                        google_sheet_file_id: activeGoogleSheet.googleFileId || undefined,
                    }
                    : undefined;
            const focusedDocsForTurn = (options?.focusedDocuments || [])
                .map((d) => ({
                    id: String(d.id || '').trim(),
                    name: String(d.name || '').trim(),
                }))
                .filter((d) => d.id && d.name)
                .slice(0, 8);

            const data = await chatApi.askPageStream(
                message,
                page,
                y,
                m,
                currConversationId || null,
                activeBusinessId || undefined,
                {
                    onStatus: (event) => {
                        setMessages((prev) => {
                            const updated = [...prev];
                            const currentActivity = updated[updated.length - 1]?.activity || [];
                            const nextActivity = appendActivityStep(currentActivity, event);
                            updated[updated.length - 1] = {
                                role: 'ai',
                                content: streamedText,
                                tables: [],
                                charts: [],
                                suggested_questions: [],
                                activity: nextActivity,
                            };
                            return updated;
                        });
                    },
                    onToken: (delta) => {
                        streamedText += delta;
                        setMessages((prev) => {
                            const updated = [...prev];
                            updated[updated.length - 1] = {
                                role: 'ai',
                                content: streamedText,
                                tables: [],
                                charts: [],
                                suggested_questions: [],
                                activity: updated[updated.length - 1]?.activity || [],
                            };
                            return updated;
                        });
                    },
                },
                sheetPayload,
                undefined,
                page === 'documents'
                    ? {
                        focused_document_ids: focusedDocsForTurn.map((d) => d.id),
                        focused_document_names: focusedDocsForTurn.map((d) => d.name),
                    }
                    : undefined,
                ac.signal
            );

            if (data.conversation_id) {
                setCurrConversationId(data.conversation_id)
            }

            const responseData = data.response;
            console.log("Full API data:", data);
            console.log("Response data:", responseData);
            console.log("Response type:", typeof responseData);
            let text = '', tables: any[] = [], charts: any[] = [], suggestedQs: string[] = [];
            let responseMeta: AnswerMeta | undefined;
            let messageId: string | undefined;
            let parentUserMessageId: string | undefined;

            if (typeof responseData === 'string') {
                text = responseData;
            } else if (typeof responseData === 'object') {
                const typedResponse = responseData as ChatResponseData;
                text = typedResponse.text || streamedText || '';
                tables = typedResponse.tables || [];
                charts = typedResponse.charts || [];
                suggestedQs = typedResponse.suggested_questions || [];
                responseMeta = typedResponse.meta;
                messageId = typedResponse.meta?.message_id;
                parentUserMessageId = typedResponse.meta?.parent_user_message_id;
                const extractedPid = extractSheetProposalIdFromContent(text);
                if (extractedPid && (!responseMeta?.sheet_edit_proposal_id || responseMeta.sheet_edit_pending !== false)) {
                    // Detect the flow from the assistant message so the decision card renders
                    // even if the SSE meta was dropped mid-stream.
                    const looksAwaiting = /\bkeep\b[\s\S]*\brevert\b/i.test(text) ||
                        /applied to your google sheet/i.test(text);
                    responseMeta = {
                        ...(responseMeta || {}),
                        sheet_edit_proposal_id: responseMeta?.sheet_edit_proposal_id ?? extractedPid,
                        sheet_edit_pending: responseMeta?.sheet_edit_pending ?? !looksAwaiting,
                        sheet_edit_applied_awaiting_confirmation:
                            responseMeta?.sheet_edit_applied_awaiting_confirmation ?? looksAwaiting,
                    };
                }
                console.log("Extracted suggested questions:", suggestedQs);
            }



            setMessages((prev) => {
                const updated = [...prev];
                updated[updated.length - 1] = {
                    role: 'ai',
                    content: text,
                    tables,
                    charts,
                    suggested_questions: suggestedQs,
                    answer_meta: responseMeta,
                    message_id: messageId,
                    parent_user_message_id: parentUserMessageId,
                    activity: finalizeActivitySteps(updated[updated.length - 1]?.activity || []),
                };
                return updated;
            });
        } catch (error: any) {
            if (streamRequestGenRef.current !== gen) {
                return;
            }
            const errMsg = typeof error?.message === 'string' ? error.message : '';
            const aborted =
                error?.name === 'AbortError' ||
                errMsg.includes('Aborted') ||
                errMsg.includes('aborted a request') ||
                errMsg.includes('The operation was aborted') ||
                errMsg.includes('cancelled') ||
                errMsg.includes('canceled');
            if (aborted) {
                setMessages((prev) => {
                    const u = [...prev];
                    const last = u[u.length - 1];
                    if (last?.role === 'ai') {
                        u[u.length - 1] = {
                            ...last,
                            content: last.content?.trim()
                                ? `${last.content}\n\n_(Stopped.)_`
                                : 'Request stopped. Any pending sheet edit proposals were cancelled.',
                            activity: finalizeActivitySteps(last.activity || []),
                        };
                    }
                    return u;
                });
            } else {
                console.error('Chat error:', error?.message ?? error);
                const detail = getUserFriendlyErrorMessage(error, 'The request failed. Please try again.');
                setMessages((prev) => {
                    const u = [...prev];
                    const last = u[u.length - 1];
                    if (last?.role === 'ai') {
                        u[u.length - 1] = {
                            ...last,
                            content: last.content?.trim()
                                ? `${last.content}\n\n_(Request failed: ${detail})_`
                                : `**Request failed.** ${detail}`,
                            activity: finalizeActivitySteps(last.activity || []),
                        };
                    }
                    return u;
                });
            }
        } finally {
            if (streamRequestGenRef.current !== gen) {
                return;
            }
            setIsLoading(false);
            if (ac && streamAbortRef.current === ac) {
                streamAbortRef.current = null;
            }
        }
    };

    const handleStopDocumentsAgent = async () => {
        streamAbortRef.current?.abort();
        if (page === 'documents' && currConversationId) {
            try {
                await googleSheetsIntegrationApi.sheetEditStop(
                    currConversationId,
                    crypto.randomUUID()
                );
            } catch (e) {
                console.warn('sheetEditStop', e);
            }
        }
    };

    const handleResolveWorkspaceDragFiles = async (fileIds: string[]): Promise<FocusedDocumentChip[]> => {
        if (!activeBusinessId) {
            if (fileIds.length > 0) {
                throw new Error('Select a business before attaching documents.');
            }
            return [];
        }
        if (fileIds.length === 0) return [];
        const uniq = Array.from(new Set(fileIds.map((x) => String(x || '').trim()).filter(Boolean))).slice(0, 8);
        const settled = await Promise.allSettled(
            uniq.map((fileId) => documentsApi.getUserGlobalFile(fileId, activeBusinessId || undefined)),
        );
        const out: FocusedDocumentChip[] = [];
        const errors: string[] = [];
        for (const r of settled) {
            if (r.status === 'fulfilled') {
                const doc = r.value;
                const fileId = String(doc.file_id || '').trim();
                if (!fileId) continue;
                const name = String(doc.original_filename || doc.filename || 'Document').trim();
                const kind = doc.source_channel === 'google_sheets' ? 'google_sheet' : 'document';
                out.push({ id: fileId, name, kind });
            } else {
                const msg = r.reason instanceof Error ? r.reason.message : String(r.reason);
                if (msg) errors.push(msg);
            }
        }
        if (out.length === 0 && uniq.length > 0) {
            throw new Error(errors[0] || 'Could not load those workspace files. Refresh and try again.');
        }
        return out;
    };

    const handleUploadDroppedFiles = async (files: File[]): Promise<FocusedDocumentChip[]> => {
        if (!activeBusinessId) {
            if (files.length > 0) {
                throw new Error('Select a business before attaching files.');
            }
            return [];
        }
        if (files.length === 0) return [];
        const uploads = await Promise.allSettled(
            files.slice(0, 5).map((file) => documentsApi.uploadDocument(file, 'other', ['chat_focus'], activeBusinessId || undefined)),
        );
        const out: FocusedDocumentChip[] = [];
        const errors: string[] = [];
        for (const r of uploads) {
            if (r.status === 'fulfilled') {
                const item = r.value;
                const id = String(item.file_id || '').trim();
                const name = String(item.original_filename || item.filename || 'Document').trim();
                if (id && name) {
                    out.push({ id, name, kind: 'document' });
                }
            } else {
                const msg = r.reason instanceof Error ? r.reason.message : String(r.reason);
                if (msg) errors.push(msg);
            }
        }
        if (out.length > 0) {
            try {
                onDocumentsRefresh?.();
            } catch {
                // Non-fatal: chat focus still works even if workspace refresh callback throws.
            }
        }
        if (out.length === 0) {
            throw new Error(errors[0] || 'Upload failed for dropped files.');
        }
        if (errors.length > 0) {
            // eslint-disable-next-line no-console
            console.warn('[documents chat] partial upload failures', errors);
        }
        return out;
    };

    /**
     * Decision handler that covers both flows:
     *  - ``'pending'`` (legacy plan-then-apply): approve = apply, reject = cancel proposal.
     *  - ``'awaiting_confirmation'`` (new apply-then-confirm): approve = confirm/keep,
     *    reject = revert (roll back the already-written change).
     *
     * Both flows use client_request_id for idempotency so double-clicks / retries are safe.
     */
    const handleSheetEditDecision = async (
        proposalId: string,
        action: 'approve' | 'reject',
        mode: 'pending' | 'awaiting_confirmation' = 'pending',
    ) => {
        if (sheetDecisionBusyProposalId) return;
        const clientRequestId =
            typeof crypto !== 'undefined' && crypto.randomUUID
                ? crypto.randomUUID()
                : `finz-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        setSheetDecisionBusyProposalId(proposalId);
        try {
            if (action === 'approve') {
                const r =
                    mode === 'awaiting_confirmation'
                        ? await googleSheetsIntegrationApi.sheetEditConfirm(proposalId, clientRequestId)
                        : await googleSheetsIntegrationApi.sheetEditApply(proposalId, clientRequestId);
                if (r.status !== 'ok') {
                    setMessages((prev) => [
                        ...prev,
                        {
                            role: 'human',
                            content: `[Google Sheet edit] Approve`,
                        },
                        {
                            role: 'ai',
                            content: `**Could not approve.** ${r.message || 'Unknown error.'}`,
                        },
                    ]);
                    return;
                }
                setResolvedSheetProposalIds((prev) => ({ ...prev, [proposalId]: true }));
                setMessages((prev) => [
                    ...prev,
                    {
                        role: 'human',
                        content: `[Google Sheet edit] Approved`,
                    },
                    {
                        role: 'ai',
                        content:
                            r.message ||
                            (mode === 'awaiting_confirmation'
                                ? 'Approved. The change will remain in your Google Sheet.'
                                : 'Changes applied in Google Sheets.'),
                    },
                ]);
                void queryClient.invalidateQueries({ queryKey: ['google-sheet-edit-pending', currConversationId] });
                void queryClient.invalidateQueries({
                    queryKey: ['google-sheet-edit-awaiting-confirmation', currConversationId],
                });
                onDocumentsRefresh?.();
            } else {
                const r =
                    mode === 'awaiting_confirmation'
                        ? await googleSheetsIntegrationApi.sheetEditRevert(proposalId, clientRequestId)
                        : await googleSheetsIntegrationApi.sheetEditRejectProposal(proposalId, clientRequestId);
                if (r.status !== 'ok') {
                    setMessages((prev) => [
                        ...prev,
                        {
                            role: 'human',
                            content: `[Google Sheet edit] Reject`,
                        },
                        {
                            role: 'ai',
                            content: `**Could not reject.** ${r.message || 'Unknown error.'}`,
                        },
                    ]);
                    return;
                }
                setResolvedSheetProposalIds((prev) => ({ ...prev, [proposalId]: true }));
                setMessages((prev) => [
                    ...prev,
                    {
                        role: 'human',
                        content: `[Google Sheet edit] Rejected`,
                    },
                    {
                        role: 'ai',
                        content:
                            r.message ||
                            (mode === 'awaiting_confirmation'
                                ? 'Rejected. Reverted the last Google Sheet edit to the previous values.'
                                : 'Proposal cancelled. No changes were made to Google Sheets.'),
                    },
                ]);
                void queryClient.invalidateQueries({ queryKey: ['google-sheet-edit-pending', currConversationId] });
                void queryClient.invalidateQueries({
                    queryKey: ['google-sheet-edit-awaiting-confirmation', currConversationId],
                });
                if (mode === 'awaiting_confirmation') onDocumentsRefresh?.();
            }
        } catch (error: unknown) {
            const detailStr = getUserFriendlyErrorMessage(error, 'The request failed. Please try again.');
            setMessages((prev) => [
                ...prev,
                {
                    role: 'human',
                    content: action === 'approve' ? '[Google Sheet edit] Approve' : '[Google Sheet edit] Reject',
                },
                {
                    role: 'ai',
                    content: `**Request failed.** ${detailStr}`,
                },
            ]);
        } finally {
            setSheetDecisionBusyProposalId(null);
        }
    };

    /**
     * Document-agent v2: approve/reject an *aggregate run* (one card per run,
     * regardless of how many iterations it took). Approve confirms every child
     * proposal in the run; reject reverts them in LIFO order and records a
     * typed lesson for future planning. Idempotent via client_request_id.
     */
    const handleSheetEditRunDecision = async (
        runId: string,
        action: 'approve' | 'reject',
    ) => {
        if (sheetDecisionBusyRunId) return;
        if (!currConversationId) return;
        const clientRequestId =
            typeof crypto !== 'undefined' && crypto.randomUUID
                ? crypto.randomUUID()
                : `finz-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        setSheetDecisionBusyRunId(runId);
        try {
            const r =
                action === 'approve'
                    ? await googleSheetsIntegrationApi.agentRunConfirm(runId, clientRequestId)
                    : await googleSheetsIntegrationApi.agentRunRevert(
                        runId,
                        currConversationId,
                        clientRequestId,
                        '',
                    );
            if (r.status !== 'ok') {
                setMessages((prev) => [
                    ...prev,
                    {
                        role: 'human',
                        content:
                            action === 'approve'
                                ? '[Google Sheet edit] Approve'
                                : '[Google Sheet edit] Reject',
                    },
                    {
                        role: 'ai',
                        content: `**Could not ${action}.** ${r.message || 'Unknown error.'}`,
                    },
                ]);
                return;
            }
            setResolvedSheetRunIds((prev) => ({ ...prev, [runId]: true }));
            setMessages((prev) => [
                ...prev,
                {
                    role: 'human',
                    content:
                        action === 'approve'
                            ? '[Google Sheet edit] Approved'
                            : '[Google Sheet edit] Rejected',
                },
                {
                    role: 'ai',
                    content:
                        r.message ||
                        (action === 'approve'
                            ? 'Approved. The changes will remain in your Google Sheet.'
                            : 'Rejected. Reverted every iteration in this run.'),
                },
            ]);
            void queryClient.invalidateQueries({ queryKey: ['google-sheet-edit-awaiting-confirmation', currConversationId] });
            void refetchAgentState();
            onDocumentsRefresh?.();
        } catch (error: unknown) {
            const detail = getUserFriendlyErrorMessage(error, 'The request failed. Please try again.');
            setMessages((prev) => [
                ...prev,
                {
                    role: 'human',
                    content:
                        action === 'approve'
                            ? '[Google Sheet edit] Approve'
                            : '[Google Sheet edit] Reject',
                },
                { role: 'ai', content: `**Request failed.** ${detail}` },
            ]);
        } finally {
            setSheetDecisionBusyRunId(null);
        }
    };

    /**
     * User answered the orchestrator's clarification — route it back through
     * the normal chat pipeline so the specialist calls
     * `consume_pending_clarification` and the merged turn re-enters
     * planner-act-verify with the new context attached.
     */
    const handleClarificationSubmit = (answer: string) => {
        const trimmed = answer.trim();
        if (!trimmed) return;
        void handleSendMessage(trimmed);
    };

    /** Cancel the parked clarification; locally hides the card and tells the backend to drop it. */
    const handleClarificationDismiss = async () => {
        if (!currConversationId) return;
        const key = agentState?.pending_clarification?.question || 'dismissed';
        setDismissedClarificationKeys((prev) => ({ ...prev, [key]: true }));
        const clientRequestId =
            typeof crypto !== 'undefined' && crypto.randomUUID
                ? crypto.randomUUID()
                : `finz-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        try {
            await googleSheetsIntegrationApi.answerClarification(
                currConversationId,
                '',
                clientRequestId,
            );
        } catch (e) {
            console.warn('answerClarification dismiss failed', e);
        }
        void refetchAgentState();
    };

    const handleRecycleSuggestions = async (msgIndex: number) => {
        try {
            const data = await qboApi.getSampleQuestions();
            const allQuestions = data.sample_questions || [];

            // Randomly select 3 unique questions
            const shuffled = [...allQuestions].sort(() => 0.5 - Math.random());
            const selected = shuffled.slice(0, 3).map(q => q.question);

            setMessages((prev) => {
                const updated = [...prev];
                if (updated[msgIndex]) {
                    updated[msgIndex] = {
                        ...updated[msgIndex],
                        suggested_questions: selected
                    };
                }
                return updated;
            });
        } catch (error) {
            console.error("Failed to recycle suggestions:", error);
        }
    };

    const findRefreshQuestion = (msgIndex: number): string => {
        const msg = messages[msgIndex];
        const parentId = msg?.answer_meta?.parent_user_message_id || msg?.parent_user_message_id;
        if (parentId) {
            const parentMsg = messages.find((m) => m.role === 'human' && m.message_id === parentId);
            if (parentMsg?.content) return parentMsg.content;
        }
        for (let i = msgIndex - 1; i >= 0; i -= 1) {
            if (messages[i]?.role === 'human') return messages[i].content;
        }
        return '';
    };

    const handleSubmitFeedback = async (
        msgIndex: number,
        feedback: FeedbackType,
        reasonCode?: FeedbackReasonCode,
        reasonNote?: string,
    ) => {
        const msg = messages[msgIndex];
        if (!msg?.message_id || !currConversationId) return;
        try {
            await chatApi.submitAnswerFeedback(
                {
                    conversation_id: currConversationId,
                    answer_message_id: msg.message_id,
                    feedback,
                    reason_code: reasonCode,
                    reason_note: reasonNote,
                    page,
                    year: y,
                    month: m,
                    answer_meta: msg.answer_meta as Record<string, unknown>,
                },
                activeBusinessId || undefined,
            );
            setMessages((prev) => {
                const updated = [...prev];
                if (!updated[msgIndex]) return prev;
                updated[msgIndex] = {
                    ...updated[msgIndex],
                    feedback_state: {
                        submitted: true,
                        feedback,
                        reason_code: reasonCode,
                    },
                };
                return updated;
            });
        } catch (error) {
            console.error("Failed to submit feedback:", error);
        }
    };

    const handleRefreshAnswer = async (msgIndex: number, variant: RefreshVariant) => {
        const msg = messages[msgIndex];
        if (!msg?.message_id || !currConversationId) return;
        const question = findRefreshQuestion(msgIndex);
        if (!question) return;

        let streamedText = '';
        const targetMessageId = msg.message_id;
        const updateByMessageId = (updater: (current: Message) => Message) => {
            setMessages((prev) =>
                prev.map((item) => (item.message_id === targetMessageId ? updater(item) : item))
            );
        };

        try {
            await chatApi.refreshAnswerStream(
                currConversationId,
                targetMessageId,
                question,
                page,
                y,
                m,
                variant,
                (msg.answer_meta as Record<string, unknown>) || undefined,
                activeBusinessId || undefined,
                {
                    onStatus: (event) => {
                        updateByMessageId((current) => ({
                            ...current,
                            content: streamedText || current.content,
                            activity: appendActivityStep(current.activity || [], event),
                        }));
                    },
                    onToken: (delta) => {
                        streamedText += delta;
                        updateByMessageId((current) => ({
                            ...current,
                            content: streamedText,
                            activity: current.activity || [],
                        }));
                    },
                },
            ).then((payload) => {
                const responseData = payload.response as ChatResponseData;
                updateByMessageId((current) => ({
                    ...current,
                    content: responseData?.text || streamedText || current.content,
                    tables: responseData?.tables || [],
                    charts: responseData?.charts || [],
                    suggested_questions: responseData?.suggested_questions || [],
                    answer_meta: responseData?.meta,
                    message_id: responseData?.meta?.message_id || current.message_id,
                    parent_user_message_id: responseData?.meta?.parent_user_message_id || current.parent_user_message_id,
                    activity: finalizeActivitySteps(current.activity || []),
                }));
            });
        } catch (error) {
            console.error("Failed to refresh answer:", error);
        }
    };

    const handleNewChat = () => {
        // Clear State
        setMessages([]);
        setCurrConversationId(null);
        setResolvedSheetProposalIds({});
        setSheetDecisionBusyProposalId(null);
        setResolvedSheetRunIds({});
        setSheetDecisionBusyRunId(null);
        setDismissedClarificationKeys({});
        // Keep pageSummary (don't clear it)

        // Refetch initial prompts for the fresh chat
        if (initialPrompts && initialPrompts.length > 0) {
            setInitPrompts(initialPrompts);
        } else {
            chatApi.getPrompts()
                .then(res => setInitPrompts(res.map(p => p.question)))
                .catch(err => console.error('Failed to load init prompts:', err));
        }

        // Clear Storage (namespaced keys for current user)
        if (chatStorageKeys) {
            sessionStorage.removeItem(chatStorageKeys.msgs);
            sessionStorage.removeItem(chatStorageKeys.id);
        }
    };

    const handlePromptSelect = (prompt: string) => {
        setShowPromptLibrary(false);
        handleSendMessage(prompt);
    };

    const renderMessages = () => (
        messages.map((msg, idx) => (
            <div key={idx} className="py-4">
                {msg.role === 'human' ? (
                    msg.content.startsWith("FILE: ") ? (
                        // --- NEW FILE MESSAGE UI ---
                        <div className="flex w-full justify-end">
                            <div className="w-full max-w-[200px] border border-gray-200 rounded-lg p-3 flex items-center gap-3 bg-white shadow-sm">
                                {/* 1. Dynamic Icon */}
                                {(() => {
                                    const fileName = msg.content.replace("FILE: ", "");
                                    const { Icon, color, bg } = getFileIconConfig(fileName);
                                    return (
                                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${bg}`}>
                                            <Icon className={`w-6 h-6 ${color}`} />
                                        </div>
                                    );
                                })()}
                                {/* 2. File Info */}
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900 truncate mb-1">
                                        {msg.content.replace("FILE: ", "")}
                                    </p>
                                    <div className="flex items-center gap-2 text-xs text-gray-500">
                                        <span className="flex items-center gap-1 text-green-600 font-medium">
                                            <CheckCircle2 className="w-3 h-3" /> Completed
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        // Normal Human Message
                        <div className="flex w-full justify-end">
                            <div className={`p-4 w-fit bg-gray-100 rounded-lg ${CHAT_HUMAN_TEXT_CLASS}`}>
                                {msg.content}
                            </div>
                        </div>
                    )
                ) : (
                    <>
                        <AiMessage
                            msg={msg}
                            onSuggestionClick={handleSendMessage}
                            onRecycleSuggestions={() => handleRecycleSuggestions(idx)}
                            onFeedback={(feedback, reasonCode, reasonNote) =>
                                handleSubmitFeedback(idx, feedback, reasonCode, reasonNote)
                            }
                            onRefresh={(variant) => handleRefreshAnswer(idx, variant)}
                            showSuggestedQuestions={page !== 'documents'}
                        />
                        {page === 'documents' &&
                            cfoAgentWritesEnabled &&
                            (() => {
                                const contentStr = typeof msg.content === 'string' ? msg.content : '';
                                // Prefer the ids the backend sent with this specific assistant message.
                                // Fall back to (a) parsing the markdown (for older messages that lack
                                // meta) and (b) polling endpoints (for the most recent message when the
                                // SSE meta was lost mid-stream).
                                const metaAwaiting = Boolean(
                                    msg.answer_meta?.sheet_edit_applied_awaiting_confirmation,
                                );
                                const isLastAi = idx === lastAiMessageIndex;

                                // ---- Document-agent v2: run-level Approve/Reject card ----
                                // When the backend tagged this assistant message with an aggregate
                                // run id, render the one-card-per-run variant (with collapsible
                                // iteration summaries) and short-circuit the legacy per-proposal
                                // pathway — the run-level confirm/revert handles every child
                                // proposal atomically.
                                const runIdFromMeta = msg.answer_meta?.sheet_edit_run_id || null;
                                // Only attach the run id from message meta when the backend
                                // also reports actually-applied proposal ids. A failed run
                                // (e.g. orchestrator error, unsupported structural op) may
                                // still carry a run id internally, but there is nothing to
                                // approve or revert so the card must not render.
                                const metaAppliedIds = (msg.answer_meta?.sheet_edit_applied_proposal_ids as
                                    | string[]
                                    | undefined) || [];
                                const runIdFromMetaSafe = metaAppliedIds.length > 0 ? runIdFromMeta : null;
                                const awaitingRunFallback =
                                    isLastAi && agentState?.awaiting_confirmation_run?.run_id
                                        ? agentState.awaiting_confirmation_run.run_id
                                        : null;
                                const runId = runIdFromMetaSafe || awaitingRunFallback;
                                if (runId && !resolvedSheetRunIds[runId]) {
                                    const summaries =
                                        (msg.answer_meta?.sheet_edit_iteration_summaries as
                                            | SheetEditIterationSummary[]
                                            | undefined) ||
                                        (awaitingRunFallback && agentState?.awaiting_confirmation_run?.iteration_summaries) ||
                                        [];
                                    const busy = sheetDecisionBusyRunId === runId;
                                    return (
                                        <div className="mt-3 max-w-6xl rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 sm:px-4">
                                            <label
                                                htmlFor={`sheet-run-decision-${runId}`}
                                                className="mb-1.5 block text-xs font-medium tracking-wide text-slate-600"
                                            >
                                                Google Sheet change
                                            </label>
                                            <div className="flex flex-wrap items-center gap-2">
                                                <select
                                                    id={`sheet-run-decision-${runId}`}
                                                    className="min-w-[220px] rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-400 disabled:cursor-not-allowed disabled:opacity-60"
                                                    defaultValue=""
                                                    disabled={busy}
                                                    onChange={(e) => {
                                                        const v = e.target.value;
                                                        if (v !== 'approve' && v !== 'reject') return;
                                                        e.target.selectedIndex = 0;
                                                        void handleSheetEditRunDecision(runId, v);
                                                    }}
                                                >
                                                    <option value="">Choose: Approve or Reject</option>
                                                    <option value="approve">Approve — keep the change in Google Sheets</option>
                                                    <option value="reject">Reject — revert the change in Google Sheets</option>
                                                </select>
                                                {busy && (
                                                    <LoaderCircle className="h-4 w-4 shrink-0 animate-spin text-slate-500" aria-hidden />
                                                )}
                                            </div>
                                            <p className="mt-1.5 text-[11px] text-slate-500">
                                                You can also type approve or reject in the chat box.
                                            </p>
                                            <IterationSummaryList summaries={summaries as SheetEditIterationSummary[]} />
                                        </div>
                                    );
                                }
                                // ---- End v2 run-level card ----

                                const fallbackPid =
                                    isLastAi && looksLikeSheetProposalAssistantMessage(contentStr)
                                        ? awaitingConfirmationSheetProposal?.proposal_id ||
                                        pendingSheetProposal?.proposal_id ||
                                        null
                                        : null;
                                const pid =
                                    msg.answer_meta?.sheet_edit_proposal_id ??
                                    extractSheetProposalIdFromContent(contentStr) ??
                                    fallbackPid;
                                if (!pid || resolvedSheetProposalIds[pid]) return null;
                                // Explicit pending=false means the assistant has already resolved the
                                // proposal (applied + no awaiting confirmation). Hide the card.
                                if (
                                    msg.answer_meta?.sheet_edit_pending === false &&
                                    !metaAwaiting
                                ) {
                                    return null;
                                }
                                // Decide mode:
                                //   * If the backend tagged this message as awaiting confirmation, the
                                //     change is already in the sheet — show Keep/Revert.
                                //   * Else, if the fallback poll picked up an awaiting-confirmation id
                                //     for the most recent message (e.g. page reload), use that mode.
                                //   * Otherwise fall back to the legacy Approve/Reject (pending plan).
                                const awaitingPollMatches =
                                    isLastAi &&
                                    awaitingConfirmationSheetProposal?.proposal_id === pid;
                                const mode: 'pending' | 'awaiting_confirmation' =
                                    metaAwaiting || awaitingPollMatches
                                        ? 'awaiting_confirmation'
                                        : 'pending';
                                const busy = sheetDecisionBusyProposalId === pid;
                                const isAwaitingMode = mode === 'awaiting_confirmation';
                                // Keep the card shape/labels identical to the legacy Approve/Reject UI.
                                // Under the apply-then-confirm flow, Approve = keep the already-applied
                                // change, and Reject = revert it. The user-facing verbs do not change.
                                const headerLabel = 'Google Sheet change';
                                const placeholder = 'Choose: Approve or Reject';
                                const approveOption = isAwaitingMode
                                    ? 'Approve — keep the change in Google Sheets'
                                    : 'Approve — write to Google Sheets';
                                const rejectOption = isAwaitingMode
                                    ? 'Reject — revert the change in Google Sheets'
                                    : 'Reject — cancel this proposal';
                                const hint = 'You can also type approve or reject in the chat box.';
                                return (
                                    <div className="mt-3 max-w-6xl rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 sm:px-4">
                                        <label
                                            htmlFor={`sheet-decision-${pid}`}
                                            className="mb-1.5 block text-xs font-medium tracking-wide text-slate-600"
                                        >
                                            {headerLabel}
                                        </label>
                                        <div className="flex flex-wrap items-center gap-2">
                                            <select
                                                id={`sheet-decision-${pid}`}
                                                className="min-w-[220px] rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-400 disabled:cursor-not-allowed disabled:opacity-60"
                                                defaultValue=""
                                                disabled={busy}
                                                onChange={(e) => {
                                                    const v = e.target.value;
                                                    if (v !== 'approve' && v !== 'reject') return;
                                                    e.target.selectedIndex = 0;
                                                    void handleSheetEditDecision(pid, v, mode);
                                                }}
                                            >
                                                <option value="">{placeholder}</option>
                                                <option value="approve">{approveOption}</option>
                                                <option value="reject">{rejectOption}</option>
                                            </select>
                                            {busy && (
                                                <LoaderCircle className="h-4 w-4 shrink-0 animate-spin text-slate-500" aria-hidden />
                                            )}
                                        </div>
                                        <p className="mt-1.5 text-[11px] text-slate-500">{hint}</p>
                                    </div>
                                );
                            })()}
                    </>
                )}
            </div>
        ))
    );

    return (
        <div
            className={`flex flex-col bg-white overflow-hidden ${isExpanded ? 'fixed inset-y-0 left-16 right-0 z-[1000]' : 'relative h-full'}`}
            style={{ height: isExpanded || isMobile ? '100dvh' : 'var(--chat-height, 100dvh)' }}
        >
            {/* Prompt Library Modal */}
            {showPromptLibrary && (
                <PromptLibraryModal
                    onClose={() => setShowPromptLibrary(false)}
                    onSelect={handlePromptSelect}
                />
            )}

            {/* Header */}
            {/* Header */}
            <div className="h-[48px] px-4 flex items-center justify-between bg-[#F1F2F4] border-b border-gray-100 flex-shrink-0 z-10">
                {/* Left Side: Title & Beta Badge */}
                <div className="flex items-center gap-1">
                    <AISparkle size={17} />
                    <h2 className="text-sm font-bold text-slate-800">AI CFO</h2>
                </div>

                {/* Right Side: Action Icons */}
                <div className="flex items-center gap-3 text-gray-500">
                    {!isMobile && (
                        <button
                            onClick={() => setIsExpanded(!isExpanded)}
                            className="hover:text-slate-800 transition-colors"
                            title={isExpanded ? "Shrink" : "Expand"}
                        >
                            {isExpanded ? <Minimize2 size={18} /> : <Expand size={18} />}
                        </button>
                    )}
                    <button
                        onClick={handleNewChat}
                        className="hover:text-slate-800 transition-colors"
                        title="New Chat"
                    >
                        <SquarePen size={18} />
                    </button>
                    <button
                        onClick={() => setShowPromptLibrary(true)}
                        className="hover:text-slate-800 transition-colors"
                        title="Prompt Library"
                    >
                        <LibraryBig size={18} />
                    </button>
                    <button
                        onClick={() => {
                            setIsExpanded(false);
                            onClose?.();
                        }}
                        className="hover:text-slate-800 transition-colors"
                        title="Close"
                    >
                        <X size={18} />
                    </button>
                </div>
            </div>

            {/* Scrollable Message Area */}
            <div className="flex-1 overflow-y-auto px-4 scroll-smooth text-sm text-gray-900 chat-scrollbar-invisible">
                <div className="w-full max-w-6xl mx-auto sm:px-2 pt-4">
                    {/* Page Summary Block (margin / cashflow only — avoids showing cached cash summary on Documents) */}
                    {showCashflowStyleSummary && (pageSummary || isSummaryLoading) && (
                        <div className="relative my-4 p-4 bg-indigo-50 border border-indigo-100 rounded-lg text-sm leading-relaxed text-gray-800 shadow-sm">
                            {isSummaryLoading && (
                                <h4 className="font-semibold text-[#1F2937] mb-2 flex items-center gap-2 w-full">
                                    <LoadingDots />
                                    Generating Summary...
                                </h4>
                            )}
                            {!isSummaryLoading && (
                                <button
                                    onClick={handleRefreshSummary}
                                    className="absolute top-4 right-4 text-[#1F2937] hover:text-black transition-colors p-1 z-10"
                                    title="Refresh Summary"
                                >
                                    <RefreshCw size={16} />
                                </button>
                            )}
                            {!isSummaryLoading && <AgentMarkdown content={pageSummary || ''} />}
                        </div>
                    )}
                    {page === 'documents' && messages.length === 0 && !isLoading && (
                        <div className="mb-4 rounded-xl border border-gray-100 bg-gray-50/90 px-4 py-3 text-sm leading-relaxed text-gray-700">
                            Hi, I&apos;m your Document Agent. I can answer questions about your files and Google Sheets from
                            imported data.
                            {cfoAgentWritesEnabled ? (
                                <> Ask a question or point out anything that looks off; I can also propose sheet edits.</>
                            ) : (
                                <>
                                    {' '}
                                    Automated spreadsheet editing in chat is under development — ask questions in read-only
                                    mode for now.
                                </>
                            )}
                        </div>
                    )}
                    {page === 'documents' &&
                        sidebarOpen &&
                        (isDocumentsWorkspaceSummaryLoading || documentsWorkspaceSummary) && (
                            <div className="relative my-4 rounded-lg border border-indigo-100 bg-indigo-50 p-4 text-sm leading-relaxed text-gray-800 shadow-sm">
                                {!isDocumentsWorkspaceSummaryLoading && (
                                    <button
                                        type="button"
                                        onClick={() => void refetchDocumentsWorkspaceSummary()}
                                        className="absolute right-4 top-4 z-10 p-1 text-[#1F2937] transition-colors hover:text-black"
                                        title="Refresh summary"
                                    >
                                        <RefreshCw size={16} />
                                    </button>
                                )}
                                <h1 className="mb-2 pr-8 text-sm font-semibold text-gray-900">AI Powered Insights</h1>
                                {isDocumentsWorkspaceSummaryLoading && (
                                    <div className="flex items-center gap-2 font-semibold text-[#1F2937]">
                                        <LoadingDots />
                                        <span>Generating Summary...</span>
                                    </div>
                                )}
                                {!isDocumentsWorkspaceSummaryLoading && documentsWorkspaceSummary && (
                                    <div className="space-y-1">
                                        <p className="text-slate-800 leading-relaxed">
                                            {documentsWorkspaceSummary.summary}
                                        </p>
                                        <p className="text-xs text-slate-500">
                                            <span className="font-semibold tabular-nums">
                                                {documentsWorkspaceSummary.stats.total_documents}
                                            </span>{' '}
                                            documents ·{' '}
                                            <span className="font-semibold tabular-nums">
                                                {(documentsWorkspaceSummary.stats.vendors_from_invoices || []).length}
                                            </span>{' '}
                                            vendors
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}
                    {/* Initial Suggested Prompts (shown when no messages) */}
                    {messages.length === 0 && !isLoading && initPrompts.length > 0 && (
                        <div className="flex flex-col gap-2 mt-6 mb-6">
                            <p className="text-xs font-medium tracking-wide mb-1">Suggested Prompts</p>
                            {initPrompts.slice(0, 3).map((prompt, index) => (
                                <button
                                    key={index}
                                    onClick={() => handleSendMessage(prompt)}
                                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-100 hover:border-gray-300 transition text-left"
                                >
                                    {prompt}
                                </button>
                            ))}
                        </div>
                    )}
                    {renderMessages()}
                    {shouldShowLoadingDots && (
                        <div className="py-3">
                            <div className="w-full max-w-[350px] sm:max-w-full space-y-3 text-black">
                                <LoadingDots />
                            </div>
                        </div>
                    )}
                    {/* The strip is gated to persistent state only ("ready"
                        / "needs your input" / awaiting approval). While the
                        main Activity timeline is streaming we suppress it
                        entirely so the user never sees two progress boxes
                        for the same turn. */}
                    {page === 'documents' && cfoAgentWritesEnabled && !lastAiHasActiveActivity && (
                        <AgentStatusStrip
                            message={deriveAgentStatusMessage(
                                agentTrace,
                                agentState?.awaiting_confirmation_run?.run_id || null,
                            )}
                        />
                    )}
                    {page === 'documents' &&
                        cfoAgentWritesEnabled &&
                        agentState?.pending_clarification &&
                        !dismissedClarificationKeys[agentState.pending_clarification.question] && (
                            <ClarificationCard
                                question={agentState.pending_clarification.question}
                                originalRequest={agentState.pending_clarification.original_request}
                                choices={agentState.pending_clarification.choices}
                                busy={isLoading}
                                onCorrect={handleClarificationSubmit}
                                onDismiss={handleClarificationDismiss}
                                correctPlaceholder="Type your answer…"
                                correctLabel="Answer"
                            />
                        )}
                    {(() => {
                        // Read-path schema HITL: the last AI message may carry a
                        // ``sheet_schema_low_confidence`` payload listing any
                        // tabs whose detected schema is below the trust
                        // threshold. Render one dropdown card per tab under the
                        // answer (non-blocking — the answer itself is already
                        // shown above). Dismissing a tab stashes the key so we
                        // don't re-prompt for the same row.
                        if (page !== 'documents') return null;
                        const sourceDocumentId = activeGoogleSheet?.sourceDocumentId;
                        if (!sourceDocumentId) return null;
                        const lastAi = [...messages].reverse().find((m) => m.role === 'ai');
                        const tabs = (lastAi?.answer_meta?.sheet_schema_low_confidence || []) as Array<{
                            tab_name: string;
                            confidence: number;
                            source: string;
                            header_rows?: number[];
                            columns?: string[];
                        }>;
                        if (!tabs || tabs.length === 0) return null;
                        return tabs.map((tab) => {
                            const key = `schema:${sourceDocumentId}:${tab.tab_name}`;
                            if (dismissedClarificationKeys[key]) return null;
                            const pct = Math.round((tab.confidence || 0) * 100);
                            const headerRows =
                                (tab.header_rows && tab.header_rows.length > 0
                                    ? tab.header_rows.map((r) => r + 1).join(', ')
                                    : 'unknown');
                            const columnsPreview = (tab.columns || []).slice(0, 6).join(', ');
                            return (
                                <ClarificationCard
                                    key={key}
                                    question={`Does "${tab.tab_name}" look right?`}
                                    subtitle={
                                        <>
                                            <div>Detected headers on row {headerRows}. Confidence {pct}%.</div>
                                            {columnsPreview && (
                                                <div className="truncate">
                                                    Columns: {columnsPreview}
                                                    {(tab.columns || []).length > 6 ? '…' : ''}
                                                </div>
                                            )}
                                        </>
                                    }
                                    busy={isLoading}
                                    correctPlaceholder="e.g. headers are in row 2, or row 1 and row 2"
                                    approveLabel="Approve"
                                    correctLabel="Make correction"
                                    onApprove={async () => {
                                        setDismissedClarificationKeys((prev) => ({ ...prev, [key]: true }));
                                        try {
                                            await googleSheetsIntegrationApi.confirmSheetContext(
                                                sourceDocumentId,
                                                tab.tab_name,
                                            );
                                        } catch (e) {
                                            console.warn('confirmSheetContext failed', e);
                                        }
                                    }}
                                    onCorrect={(text) => {
                                        setDismissedClarificationKeys((prev) => ({ ...prev, [key]: true }));
                                        void handleSendMessage(
                                            `For tab "${tab.tab_name}", ${text}`,
                                        );
                                    }}
                                    onDismiss={() =>
                                        setDismissedClarificationKeys((prev) => ({ ...prev, [key]: true }))
                                    }
                                    footer={
                                        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
                                            <span className="min-w-0 flex-1 basis-full sm:basis-auto">
                                                This won't re-run the agent. Corrections update the sheet context and apply to future turns.
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    try {
                                                        window.dispatchEvent(
                                                            new CustomEvent('finz:open-sheet-context', {
                                                                detail: { sourceDocumentId },
                                                            }),
                                                        );
                                                    } catch (e) {
                                                        console.warn('open-sheet-context dispatch failed', e);
                                                    }
                                                }}
                                                className="inline-flex shrink-0 items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-[11px] font-medium text-slate-700 shadow-sm transition hover:border-slate-400 hover:bg-slate-50"
                                            >
                                                Open context
                                            </button>
                                        </div>
                                    }
                                />
                            );
                        });
                    })()}
                    <div ref={chatEndRef} />
                </div>
            </div>

            {/* Sticky Input Area */}
            <div className="w-full bg-white border-t border-gray-100 p-4 pb-6 flex-shrink-0 z-10">
                <div className="w-full max-w-6xl mx-auto">
                    <InputBox
                        onSendMessage={handleSendMessage}
                        variant={page === 'documents' ? 'documents' : 'default'}
                        onDocumentsRefresh={page === 'documents' ? onDocumentsRefresh : undefined}
                        onGoogleSheetsImportQueued={page === 'documents' ? onGoogleSheetsImportQueued : undefined}
                        documentsFolderLabel={page === 'documents' ? documentsFolderLabel : undefined}
                        documentsAgentLoading={documentsAgentBusy}
                        onStopDocumentsAgent={page === 'documents' ? handleStopDocumentsAgent : undefined}
                        focusedDocuments={page === 'documents' ? focusedDocuments : []}
                        onFocusedDocumentsChange={page === 'documents' ? setFocusedDocuments : undefined}
                        onResolveWorkspaceDragFiles={page === 'documents' ? handleResolveWorkspaceDragFiles : undefined}
                        onUploadDroppedFiles={page === 'documents' ? handleUploadDroppedFiles : undefined}
                    />
                </div>
            </div>
        </div>
    );
};

export default ChatPanel;
